import {
  Children,
  forwardRef,
  Key,
  ReactElement,
  RefObject,
  UIEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  List,
  ListImperativeAPI,
  useDynamicRowHeight,
  useListRef,
} from "react-window";

import { useRtl } from "@ryde/redux";
import { useScheduleDynamicRowHeightUpdate } from "@ryde/shared";

import { ListBoxVirtualizedRow } from "./list-box-virtualized-row.component";
import {
  DEFAULT_ROW_HEIGHT,
  LISTBOX_OVERSCAN_COUNT,
  LISTBOX_VERTICAL_PADDING,
} from "./list-box.constants";
import { IListBoxProps, IListBoxRowProps } from "./list-box.models";
import {
  buildListLayoutSignature,
  getListViewportHeight,
  getVirtualListItemKey,
  mergeRefs,
  resolveEstimatedItemSize,
  scrollVirtualListToHighlightedOption,
} from "./list-box.utils";
import { useListboxUserScroll } from "./use-listbox-user-scroll";

export const ListBox = forwardRef<HTMLDivElement, IListBoxProps>(
  (
    {
      children,
      size = DEFAULT_ROW_HEIGHT,
      spacing = 0,
      listClassName,
      dynamicHeight = false,
      dynamicHeightRef,
      ...rest
    }: IListBoxProps,
    ref,
  ) => {
    const { direction } = useRtl();
    const listRef = useListRef() as RefObject<ListImperativeAPI>;

    const items = useMemo(() => Children.toArray(children), [children]);
    const rowCount = items.length;
    const listLayoutSignature = useMemo(
      () => buildListLayoutSignature(items),
      [items],
    );
    const estimatedItemSize = resolveEstimatedItemSize(size);
    const outerElementRef = useRef<HTMLDivElement | null>(null);
    const [outerScrollElement, setOuterScrollElement] =
      useState<HTMLDivElement | null>(null);

    const dynamicRowHeight = useDynamicRowHeight({
      defaultRowHeight: estimatedItemSize,
      key: listLayoutSignature,
    });

    const scheduleRowHeightUpdate =
      useScheduleDynamicRowHeightUpdate(dynamicRowHeight);

    const setSize = useCallback(
      (index: number, measuredHeight: number) => {
        const previousHeight = dynamicRowHeight.getRowHeight(index);
        if (previousHeight === measuredHeight) {
          return;
        }

        scheduleRowHeightUpdate(index, measuredHeight);
      },
      [dynamicRowHeight, scheduleRowHeightUpdate],
    );

    const scrollHighlightedOptionIntoView = useCallback(() => {
      const listInstance = listRef.current;
      const outerElement = outerElementRef.current;
      if (!listInstance || !outerElement) {
        return;
      }

      scrollVirtualListToHighlightedOption(listInstance, outerElement);
    }, [listRef]);

    const isUserScrollingRef = useListboxUserScroll({
      outerElement: outerScrollElement,
      resetKey: listLayoutSignature,
      onUserScrollEnd: scrollHighlightedOptionIntoView,
    });

    const scrollHighlightedOptionIntoViewUnlessUserScrolling =
      useCallback(() => {
        if (isUserScrollingRef.current) {
          return;
        }

        scrollHighlightedOptionIntoView();
      }, [isUserScrollingRef, scrollHighlightedOptionIntoView]);

    useEffect(() => {
      const outerElement = outerElementRef.current;
      if (!outerElement) {
        return undefined;
      }

      // MUI 9.0.1 scrolls listbox via option.offsetTop; counter with react-window scrollToRow.
      const observer = new MutationObserver(() => {
        scrollHighlightedOptionIntoViewUnlessUserScrolling();
      });

      observer.observe(outerElement, {
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => {
        observer.disconnect();
      };
    }, [
      listLayoutSignature,
      scrollHighlightedOptionIntoViewUnlessUserScrolling,
    ]);

    const rowHeight = useCallback(
      (index: number, rowProps: IListBoxRowProps): number => {
        const base =
          dynamicRowHeight.getRowHeight(index) ?? rowProps.estimatedItemSize;
        return base + rowProps.spacing;
      },
      [dynamicRowHeight],
    );

    const getRowKey = useCallback(
      (index: number, rowProps: IListBoxRowProps): Key =>
        getVirtualListItemKey(index, rowProps.items),
      [],
    );

    const listHeight =
      getListViewportHeight({
        itemCount: rowCount,
        cellSize: estimatedItemSize,
        dynamicHeight,
        dynamicHeightRef,
      }) + LISTBOX_VERTICAL_PADDING;

    const rowProps = useMemo(
      (): IListBoxRowProps => ({
        items,
        setSize,
        listLayoutSignature,
        spacing,
        estimatedItemSize,
      }),
      [items, setSize, listLayoutSignature, spacing, estimatedItemSize],
    );

    const {
      onScroll: listboxOnScroll,
      role,
      id,
      "aria-labelledby": ariaLabelledBy,
      "aria-multiselectable": ariaMultiselectable,
      onMouseDown,
      className: listboxClassName,
      style: listboxStyle,
      onResize: _listboxOnResize,
      ...listboxRest
    } = rest;

    const mergedListClassName = [listClassName, listboxClassName]
      .filter(Boolean)
      .join(" ");

    const handleScroll = useCallback(
      (event: UIEvent<HTMLDivElement>): void => {
        if (typeof listboxOnScroll === "function") {
          listboxOnScroll(event);
        }
      },
      [listboxOnScroll],
    );

    const syncOuterElementRef = useCallback(() => {
      const element = listRef.current?.element ?? null;
      outerElementRef.current = element;
      setOuterScrollElement(element);
      mergeRefs(ref)(element);
    }, [listRef, ref]);

    useLayoutEffect(() => {
      syncOuterElementRef();
    }, [listLayoutSignature, syncOuterElementRef]);

    return (
      <List
        {...listboxRest}
        listRef={listRef}
        rowComponent={ListBoxVirtualizedRow}
        rowCount={rowCount}
        rowHeight={rowHeight}
        rowKey={getRowKey}
        rowProps={rowProps}
        dir={direction}
        className={mergedListClassName || undefined}
        overscanCount={LISTBOX_OVERSCAN_COUNT}
        onScroll={handleScroll}
        role={role ?? "listbox"}
        id={id}
        aria-labelledby={ariaLabelledBy}
        aria-multiselectable={ariaMultiselectable}
        onMouseDown={onMouseDown}
        tagName="ul"
        style={{ ...listboxStyle, height: listHeight, width: "100%" }}
      />
    );
  },
);
